import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { HttpErrorResponse } from '@angular/common/http';
import {
  debounceTime,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { JsonApiLoaderService } from './json-api-loader.service';
import { GetEntityPipe, GetRequestPipe } from './+state/get-data-item.pipe';
import { JsonApiQuery } from './json-api.query';
import { DrupalIdentifier } from '@nx-icilalune/drupal-types';
import { isEqual } from '@madeinlune/ngx-operators';
import { FacadeService } from './facade.service';

export interface EntityState<T> {
  entity?: T | undefined;
  loaded: boolean;
  loading?: boolean;
  error?: HttpErrorResponse;
  query?: JsonApiQuery;
  queryId?: string;
}

@Injectable()
export class EntityStore<T extends DrupalIdentifier> extends ComponentStore<
  EntityState<T>
> {
  readonly queryId$ = this.select((s) => s.queryId);
  readonly query$ = this.select((s) => s.query);
  readonly entity$ = this.select((s) => s.entity).pipe(
    debounceTime(10),
    isEqual()
  );
  readonly loading$ = this.select((s) => s.loading).pipe(
    map((loading) => !!loading)
  );

  #sendRequest = this.effect((query$: Observable<JsonApiQuery | undefined>) => {
    return query$.pipe(
      tap((query) => {
        this.patchState((s) => {
          return { loading: true };
        });
      }),
      filter((query) => !!query),
      tap((query) => {
        if (query) {
          this.facade.load(query);
        }
      })
    );
  });

  setQueryId = this.effect((queryId$: Observable<string | undefined>) => {
    return queryId$.pipe(
      tap(() => {
        this.patchState({
          entity: undefined,
          loaded: false,
          loading: true,
        });
      }),
      filter((queryId) => !!queryId),
      switchMap((queryId) => {
        return this.getRequest.transform(queryId).pipe(
          filter((storeQuery) => !storeQuery?.loading),
          switchMap((query) => {
            if (query) {
              return this.getEntityPipe.transform<T>(query.id);
            }
            return of(undefined);
          })
        );
      }),
      tap((entity) => {
        if (entity) {
          this.patchState({ entity, loaded: true, loading: false });
        } else {
          this.reset();
        }
      })
    );
  });

  constructor(
    private jsonApiLoaderService: JsonApiLoaderService,
    private getEntityPipe: GetEntityPipe,
    private getRequest: GetRequestPipe,
    private facade: FacadeService
  ) {
    super({ loaded: false, loading: true });
    this.setQueryId(this.queryId$);
    this.#sendRequest(this.query$);
    // this.patchFromQuery();
  }

  setQuery(query: JsonApiQuery, entityId: string, autoLoad: boolean = false) {
    query = { ...query, id: entityId };
    if (autoLoad) {
      this.patchState({ query });
    }
    this.patchState({ queryId: `${query.queryId}-${entityId}` });
  }

  protected patchFromQuery() {
    this.patchState(
      this.query$.pipe(
        filter((storeQuery) => !storeQuery?.loading),
        switchMap((query) => {
          if (query) {
            return this.getEntityPipe.transform<T>(query?.id).pipe(
              map((entity) => {
                return { entity, loaded: true, loading: false };
              }),
              take(1)
            );
          }
          return of({ entity: undefined });
        })
      )
    );
  }

  reset() {
    this.patchState((s) => {
      return {
        entity: undefined,
        loaded: false,
        loading: true,
        query: undefined,
      };
    });
  }
}
